import type { TeleSessionSnapshot, TrackStatsSample } from '@shareview/tele-engine-core';

/**
 * Pure helpers that turn `TeleSession.getStats()` samples into the overlay rows.
 * Free of any transport so they can be unit tested with plain objects.
 */

export interface ByteSample {
  bytes: number;
  timestamp: number;
}
/** Previous byte counters keyed by direction and track id, for bitrate deltas. */
export type ByteHistory = Map<string, ByteSample>;

/** kbps from a byte counter delta; updates history. undefined on the first sample or a reset. */
export function bitrateKbps(history: ByteHistory, key: string, bytes: number, timestamp: number): number | undefined {
  const prev = history.get(key);
  history.set(key, { bytes, timestamp });
  if (!prev || timestamp <= prev.timestamp || bytes < prev.bytes) return undefined;
  return ((bytes - prev.bytes) * 8) / (timestamp - prev.timestamp); // bits/ms == kbps
}

export interface StatsRow extends TrackStatsSample {
  kbps?: number;
  /** Display name of the remote (inbound) or "me" (outbound). */
  who: string;
}

/**
 * Adds bitrates and owner names. Outbound first (video before audio), then
 * inbound grouped by participant.
 */
export function toRows(
  samples: readonly TrackStatsSample[],
  snapshot: Pick<TeleSessionSnapshot, 'remoteParticipants'>,
  history: ByteHistory,
  now: number = Date.now(),
): StatsRow[] {
  const names = new Map(snapshot.remoteParticipants.map((p) => [p.id, p.displayName || p.id]));
  const rows = samples.map((s) => ({
    ...s,
    kbps: bitrateKbps(history, `${s.direction}:${s.trackId}`, s.bytes, s.timestamp ?? now),
    who: s.direction === 'outbound' ? 'me' : (names.get(s.participantId ?? '') ?? s.participantId ?? '?'),
  }));
  const order = (r: StatsRow) => `${r.direction === 'outbound' ? 0 : 1}:${r.who}:${r.kind === 'video' ? 0 : 1}`;
  return rows.sort((a, b) => order(a).localeCompare(order(b)));
}

const n = (v: number | undefined, digits = 0) => (v === undefined || Number.isNaN(v) ? '-' : v.toFixed(digits));
const size = (w?: number, h?: number) => (w && h ? `${w}x${h}` : '-');

/**
 * Simulcast layer state from `activeEncodings`, e.g. `layers=on,on,off`. All
 * off means dynacast paused the sender because nobody is watching: 0 fps then
 * is not a device limit.
 */
export function formatLayers(r: Pick<TrackStatsSample, 'activeEncodings' | 'paused'>): string {
  if (!r.activeEncodings?.length) return 'layers=-';
  const layers = r.activeEncodings.map((a) => (a ? 'on' : 'off')).join(',');
  return r.paused ? `layers=${layers} (paused by dynacast)` : `layers=${layers}`;
}

export function formatRow(r: StatsRow): string {
  const codec = r.codec?.toUpperCase() ?? '-';
  if (r.direction === 'outbound') {
    if (r.kind === 'audio') return `out audio ${codec} ${n(r.kbps)}kbps rtt=${n(r.roundTripTimeMs)}ms`;
    return [
      'out video',
      codec,
      size(r.width, r.height),
      `${n(r.framesPerSecond)}fps`,
      `${n(r.kbps)}kbps`,
      `enc=${r.encoderImplementation ?? '-'}`,
      `ql=${r.qualityLimitationReason ?? '-'}`,
      formatLayers(r),
      `rtt=${n(r.roundTripTimeMs)}ms`,
    ].join(' ');
  }
  if (r.kind === 'audio') {
    return `in audio <${r.who}> ${codec} ${n(r.kbps)}kbps lost=${n(r.packetsLost)} jit=${n(r.jitterMs, 1)}ms`;
  }
  return [
    `in video <${r.who}>`,
    codec,
    size(r.width, r.height),
    `${n(r.framesPerSecond)}fps`,
    `${n(r.kbps)}kbps`,
    `dec=${r.decoderImplementation ?? '-'}`,
    `lost=${n(r.packetsLost)}`,
    `jit=${n(r.jitterMs, 1)}ms`,
    `freeze=${n(r.freezeCount)}`,
  ].join(' ');
}

/**
 * The selected ICE path from the first sample that has one, e.g.
 * `ice prflx/udp (relay udp) -> host/udp rtt=37ms RELAYED`. Chrome labels a relayed local candidate
 * prflx; relayProtocol is only present on relay candidates.
 */
export function formatIce(rows: readonly Pick<TrackStatsSample, 'candidatePair'>[]): string {
  const pair = rows.find((r) => r.candidatePair)?.candidatePair;
  if (!pair) return 'ice -';
  const relayed = pair.localType === 'relay' || !!pair.localRelayProtocol;
  return (
    `ice ${pair.localType ?? '?'}/${pair.localProtocol ?? '?'}${pair.localRelayProtocol ? ` (relay ${pair.localRelayProtocol})` : ''}` +
    ` -> ${pair.remoteType ?? '?'}/${pair.remoteProtocol ?? '?'} rtt=${n(pair.roundTripTimeMs)}ms${relayed ? ' RELAYED' : ''}`
  );
}

/** One compact console line, logged every 5 s. */
export function compactLine(state: string, participants: number, rows: readonly StatsRow[]): string {
  const out = rows
    .filter((r) => r.direction === 'outbound' && r.kind === 'video')
    .map((r) =>
      r.paused
        ? 'v:paused'
        : `v:${r.width ?? 0}x${r.height ?? 0}@${Math.round(r.framesPerSecond ?? 0)}/${Math.round(r.kbps ?? 0)}k` +
          `[${(r.activeEncodings ?? []).map((a) => (a ? 1 : 0)).join('')}]` +
          (r.qualityLimitationReason && r.qualityLimitationReason !== 'none' ? `(${r.qualityLimitationReason})` : ''),
    )
    .join(',');
  const inn = rows
    .filter((r) => r.direction === 'inbound' && r.kind === 'video')
    .map(
      (r) =>
        `${r.who}:${r.width ?? 0}x${r.height ?? 0}@${Math.round(r.framesPerSecond ?? 0)}/${Math.round(r.kbps ?? 0)}k lost=${r.packetsLost ?? 0} frz=${r.freezeCount ?? 0}`,
    )
    .join(',');
  return `[tele-poc] ${state} n=${participants} out=[${out}] in=[${inn}]`;
}
